import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { addMinutes, format } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBan,
  faCalendarPlus,
  faCircleInfo,
  faClock,
  faIdCard,
  faLocationDot,
  faMagnifyingGlass,
  faPlug,
  faSpinnerThird,
  faUser,
  faWrench,
} from "@fortawesome/pro-regular-svg-icons";
import ModalBase from "../../components/ModalBase";
import { FormCard, FormSection } from "../../components/FormCard";
import SelectDropdown from "../../components/SelectDropdown";
import LoadingSpinner from "../../components/LoadingSpinner";

const BOOKING_TYPE_BOOKING = "BOOKING";
const BOOKING_TYPE_UNAVAILABILITY = "UNAVAILABILITY";

const isUnavailabilityBookingType = (value) =>
  String(value || "").toUpperCase().includes(BOOKING_TYPE_UNAVAILABILITY);

const DURATION_OPTIONS = [30, 60, 90, 120];
const CANCEL_REASON_OPTIONS = [
  "POWER_OUTAGE",
  "BROKEN_CHARGER",
  "FULL",
  "BLOCKED",
  "TRAFFIC",
  "BROKEN_VEHICLE",
  "NO_CANCELED",
  "UNKNOWN",
];

const getIsChargerNotified = (booking) =>
  booking?.isChargerNotified ?? booking?.IsChargerNotified;

const getReservationStatus = (booking) =>
  String(booking?.reservationStatus ?? booking?.ReservationStatus ?? "").toUpperCase();

const getBookingSource = (booking) =>
  String(booking?.bookingSource ?? booking?.BookingSource ?? "").toUpperCase();

const isOcpiBookingSource = (booking) => getBookingSource(booking) === "OCPI";

const getBookingCustomerLabel = (booking, selectedCustomer) => {
  if (isOcpiBookingSource(booking)) {
    return booking?.customerName || "-";
  }

  return selectedCustomer?.customerName || selectedCustomer?.name || booking?.customerName || "-";
};

const getMinutesUntilBookingStart = (booking, nowMs = Date.now()) => {
  const startRaw = booking?.startDateTimeUtc;
  if (!startRaw) {
    return null;
  }

  const start = new Date(startRaw);
  if (Number.isNaN(start.getTime())) {
    return null;
  }

  return Math.floor((start.getTime() - nowMs) / 60000);
};

const formatMinutesUntilStart = (minutesUntilStart, t) => {
  if (minutesUntilStart < 0) {
    return t("booking.wizard.alreadyStarted");
  }

  if (minutesUntilStart === 0) {
    return t("booking.wizard.startsNow");
  }

  return t("booking.wizard.minutesUntilStart", { count: minutesUntilStart });
};

const evaluateBookingTermsRestrictions = (
  terms,
  minutesUntilStart,
  allowUpdate = true,
  enforceStrictly = false,
  t
) => {
  const allowAll = {
    canUpdate: true,
    canCancel: true,
    termsAlert: null,
  };

  if (!terms) {
    if (!enforceStrictly) {
      return allowAll;
    }

    return {
      canUpdate: false,
      canCancel: false,
      termsAlert: {
        title: t("booking.wizard.termsUnevaluated"),
        lines: [
          t("booking.wizard.termsNotLoaded"),
          t("booking.wizard.updateCancelBlockedUntilEval"),
        ],
      },
    };
  }

  if (minutesUntilStart === null) {
    return {
      canUpdate: !enforceStrictly && allowUpdate,
      canCancel: !enforceStrictly,
      termsAlert: {
        title: t("booking.wizard.termsUnevaluated"),
        lines: [
          t("booking.wizard.startMissing"),
          enforceStrictly
            ? t("booking.wizard.updateCancelBlocked")
            : allowUpdate
              ? t("booking.wizard.stillUpdateOrCancel")
              : t("booking.wizard.stillCancel"),
        ],
      },
    };
  }

  const changeUntilMinutes = Number(terms.changeUntilMinutes ?? 0);
  const cancelUntilMinutes = Number(terms.cancelUntilMinutes ?? 0);
  const minutesLabel = formatMinutesUntilStart(minutesUntilStart, t);

  let canUpdate = true;

  if (terms.changeNotAllowed === true) {
    canUpdate = false;
  } else if (minutesUntilStart < changeUntilMinutes) {
    canUpdate = false;
  }

  const canCancel = minutesUntilStart >= cancelUntilMinutes;

  if (canUpdate && canCancel) {
    return allowAll;
  }

  const lines = [];

  if (!canUpdate) {
    if (terms.changeNotAllowed === true) {
      lines.push(t("booking.wizard.changesNotAllowed"));
    } else if (changeUntilMinutes === 0) {
      lines.push(t("booking.wizard.changesNoLongerAllowed"));
    } else {
      lines.push(
        t("booking.wizard.changesUntilMinutes", { minutes: changeUntilMinutes })
      );
    }
  }

  if (!canCancel) {
    if (cancelUntilMinutes === 0) {
      lines.push(t("booking.wizard.cancelNoLongerAllowed"));
    } else {
      lines.push(
        t("booking.wizard.cancelUntilMinutes", { minutes: cancelUntilMinutes })
      );
    }
  }

  if (lines.length > 0) {
    lines.push(minutesLabel);
  }

  let title = t("booking.wizard.cancelBreaksTerms");
  if (!canUpdate && !canCancel) {
    title = t("booking.wizard.changeAndCancelBreakTerms");
  } else if (!canUpdate) {
    title = t("booking.wizard.changeBreaksTerms");
  }

  if (enforceStrictly) {
    if (!canUpdate && !canCancel) {
      lines.push(t("booking.wizard.updateCancelBlocked"));
    } else if (!canUpdate) {
      lines.push(t("booking.wizard.updatesBlocked"));
    } else {
      lines.push(t("booking.wizard.cancellationBlocked"));
    }
  } else {
    lines.push(
      allowUpdate
        ? t("booking.wizard.stillUpdateOrCancel")
        : t("booking.wizard.stillCancel")
    );
  }

  return {
    canUpdate,
    canCancel,
    termsAlert: {
      title,
      lines,
    },
  };
};

const mergeBookingDetails = (current, details) => ({
  ...current,
  ...details,
  isChargerNotified:
    getIsChargerNotified(details) ?? getIsChargerNotified(current),
});

const resolveActiveBooking = (resolvedBooking, contextBooking) => {
  const context = contextBooking || null;
  const contextId = context?.bookingPublicId || null;
  const resolvedId = resolvedBooking?.bookingPublicId || null;

  if (resolvedBooking && contextId && resolvedId && resolvedId !== contextId) {
    return context;
  }

  return resolvedBooking || context;
};

const getCreateDefaultStartValue = () => {
  return roundUpToQuarter(addMinutes(new Date(), 15));
};

const roundUpToQuarter = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const minutes = date.getMinutes();
  const remainder = minutes % 15;

  if (remainder !== 0 || date.getSeconds() !== 0 || date.getMilliseconds() !== 0) {
    date.setMinutes(minutes + (15 - remainder), 0, 0);
  } else {
    date.setSeconds(0, 0);
  }

  return toDateTimeLocalValue(date);
};

const toDateTimeLocalValue = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const toIsoOrNull = (value) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const getBookingEndValue = (startValue, durationMinutes) => {
  if (!startValue || !durationMinutes) {
    return "";
  }

  const startDate = new Date(startValue);

  if (Number.isNaN(startDate.getTime())) {
    return "";
  }

  return toDateTimeLocalValue(addMinutes(startDate, Number(durationMinutes)));
};

const formatDuration = (minutes, t) => {
  if (minutes >= 60) {
    const hours = minutes / 60;
    return t("booking.hoursOnly", { hours });
  }

  return t("booking.minutesOnly", { minutes });
};

const getSiteOptions = (sites, t) =>
  (Array.isArray(sites) ? sites : []).map((site) => ({
    value: site.sitePublicId,
    label: site.siteName ?? site.sitePublicId ?? t("booking.unnamedSite"),
    site,
  }));

const getConnectorOption = (connector) => ({
  value: connector?.connectorPublicId,
  label: connector?.connectorName,
  connector,
});

const formatConnectorLabelValue = (value) => {
  if (!value) {
    return null;
  }

  return String(value).replace(/_/g, " ");
};

const formatConnectorPowerSummary = (connector) => {
  const parts = [
    formatConnectorLabelValue(connector?.connectorStandard),
    connector?.maxElectricPower != null && connector.maxElectricPower !== ""
      ? `${connector.maxElectricPower} kW`
      : null,
  ].filter(Boolean);

  if (parts.length === 0 && connector?.powerType) {
    parts.push(formatConnectorLabelValue(connector.powerType));
  }

  return parts.length > 0 ? parts.join(" · ") : null;
};

const formatEvsePositionLabel = (value, t) => {
  if (!value) {
    return null;
  }

  if (value === "LEFT") {
    return t("booking.wizard.left");
  }

  if (value === "RIGHT") {
    return t("booking.wizard.right");
  }

  return formatConnectorLabelValue(value);
};

const formatBooleanLabel = (value, trueLabel, falseLabel) => {
  if (value === true) {
    return trueLabel;
  }

  if (value === false) {
    return falseLabel;
  }

  return null;
};

const DetailRow = ({ label, value }) => {
  if (value == null || value === "") {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-1 border-b border-gray-200 py-3 last:border-b-0 sm:grid-cols-[160px_1fr] sm:items-start">
      <div className="text-sm text-gray-500">{label}</div>
      <div className="text-sm font-medium text-gray-900">{value}</div>
    </div>
  );
};

const getCustomerOption = (customer, t) => ({
  value: customer?.customerPublicId,
  label: customer?.customerName ?? customer?.name ?? customer?.customerPublicId ?? t("chargeMonitor.unnamedCustomer"),
  customer,
});

const getTokenOption = (token, t) => ({
  value: token?.idTagPublicId ?? token?.publicId ?? token?.idTag ?? token?.visualNumber,
  label:
    token?.name ??
    token?.visualNumber ??
    token?.idTagPublicId ??
    t("chargeMonitor.unnamedToken"),
  token,
});

const getBookingTypeOption = (value, t) => ({
  value,
  label: value === BOOKING_TYPE_UNAVAILABILITY ? t("booking.unavailability") : t("entity.booking"),
});

const getCancelReasonOption = (value, t) => ({
  value,
  label: t(`booking.cancelReason.${value}`, { defaultValue: value.replace(/_/g, " ") }),
});

const buildSteps = (bookingType, isCustomerUser = false, lockType = false) => {
  if (isCustomerUser) {
    return ["Site", "Time", "Customer", "Summary"];
  }

  const typeSteps = lockType ? [] : ["Type"];

  if (bookingType === BOOKING_TYPE_BOOKING) {
    return ["Site", ...typeSteps, "Time", "Customer", "Summary"];
  }

  return ["Site", ...typeSteps, "Time", "Summary"];
};

const getSearchKey = (sitePublicId, startDateTime, endDateTime) => {
  return [sitePublicId || "", startDateTime || "", endDateTime || ""].join("|");
};

const getInitialState = ({
  mode,
  initialSitePublicId,
  initialStartDate,
  initialEndDate,
  booking,
  connector,
}) => {
  const initialTokenPublicIds = Array.isArray(booking?.tokenPublicIds)
    ? booking.tokenPublicIds
    : Array.isArray(booking?.tokens)
      ? booking.tokens
        .map((token) => token?.tokenPublicId || token?.idTagPublicId || null)
        .filter(Boolean)
      : [];
  const createFallbackStart = getCreateDefaultStartValue();
  const bookingStart = booking?.startDateTimeUtc || (mode === "create" ? createFallbackStart : initialStartDate) || createFallbackStart;
  const bookingEnd = booking?.endDateTimeUtc || initialEndDate || null;
  const initialBookingType = booking?.bookingType || BOOKING_TYPE_BOOKING;
  const startValue = roundUpToQuarter(toDateTimeLocalValue(bookingStart));
  const derivedDuration = bookingStart && bookingEnd
    ? Math.max(Math.round((new Date(bookingEnd).getTime() - new Date(bookingStart).getTime()) / 60000), 30)
    : 30;
  const durationMinutes = DURATION_OPTIONS.includes(derivedDuration) ? derivedDuration : 30;

  return {
    step: 0,
    sitePublicId:
      booking?.chargingSitePublicId ||
      connector?.chargingSitePublicId ||
      initialSitePublicId ||
      "",
    startDateTime: startValue,
    durationMinutes,
    bookingType: initialBookingType,
    connectorPublicId:
      booking?.chargingConnectorPublicId ||
      connector?.connectorPublicId ||
      connector?.chargingConnectorPublicId ||
      "",
    customerPublicId: booking?.customerPublicId || "",
    tokenPublicIds: initialTokenPublicIds,
    cancelReason: "",
    endDateTime:
      initialBookingType === BOOKING_TYPE_UNAVAILABILITY
        ? toDateTimeLocalValue(bookingEnd)
        : getBookingEndValue(startValue, durationMinutes),
  };
};

const BookingWizardModal = ({
  isOpen,
  onClose,
  mode = "create",
  sites,
  bookingContext,
  defaultStartDate,
  defaultEndDate,
  loadAvailableConnectors,
  loadConnectorDetails,
  loadCustomers,
  loadTokens,
  loadBookingDetails,
  loadBookingTermsForConnector,
  onCreate,
  onUpdate,
  onCancelBooking,
  isSubmitting,
  isCustomerUser = false,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [siteSearch, setSiteSearch] = useState("");
  const [sitePage, setSitePage] = useState(0);
  const SITES_PER_PAGE = 5;
  const [sitePublicId, setSitePublicId] = useState("");
  const [startDateTime, setStartDateTime] = useState("");
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [bookingType, setBookingType] = useState(BOOKING_TYPE_BOOKING);
  const [endDateTime, setEndDateTime] = useState("");
  const [connectorPublicId, setConnectorPublicId] = useState("");
  const [customerPublicId, setCustomerPublicId] = useState("");
  const [tokenPublicIds, setTokenPublicIds] = useState([]);
  const [cancelReason, setCancelReason] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [isCancelFlow, setIsCancelFlow] = useState(false);
  const [availableConnectors, setAvailableConnectors] = useState([]);
  const [availableConnectorsLoading, setAvailableConnectorsLoading] = useState(false);
  const [availableSearchKey, setAvailableSearchKey] = useState("");
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [tokens, setTokens] = useState([]);
  const [tokensLoading, setTokensLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [resolvedBooking, setResolvedBooking] = useState(null);
  const [bookingTerms, setBookingTerms] = useState(null);
  const [bookingTermsLoading, setBookingTermsLoading] = useState(false);
  const [bookingTermsLoaded, setBookingTermsLoaded] = useState(false);
  const [termsEvaluatedAtMs, setTermsEvaluatedAtMs] = useState(() => Date.now());
  const [connectorDetailsOpen, setConnectorDetailsOpen] = useState(false);
  const [connectorDetailsLoading, setConnectorDetailsLoading] = useState(false);
  const [connectorDetails, setConnectorDetails] = useState(null);
  const [connectorDetailsError, setConnectorDetailsError] = useState("");

  const contextBooking = bookingContext?.booking || null;
  const booking = useMemo(
    () => resolveActiveBooking(resolvedBooking, contextBooking),
    [contextBooking, resolvedBooking]
  );
  const isUnavailabilityEdit =
    mode === "edit" &&
    isUnavailabilityBookingType(booking?.bookingType || contextBooking?.bookingType);
  const isTypeLocked = mode === "edit";
  const lockedBookingType = isUnavailabilityEdit ? BOOKING_TYPE_UNAVAILABILITY : BOOKING_TYPE_BOOKING;
  const isOcpiBooking = mode === "edit" && isOcpiBookingSource(booking);
  const isChargerNotified = mode === "edit" && getIsChargerNotified(booking) === true;
  const isReservedStatus = getReservationStatus(booking) === "RESERVED";
  const isUpdateLockedByCharger = isChargerNotified;
  const isCancelLockedByCharger = isChargerNotified && !isReservedStatus;
  const minutesUntilBookingStart = useMemo(
    () => getMinutesUntilBookingStart(booking, termsEvaluatedAtMs),
    [booking, termsEvaluatedAtMs]
  );
  const bookingTermsRestrictions = useMemo(() => {
    const allowAll = {
      canUpdate: true,
      canCancel: true,
      termsAlert: null,
    };

    // Terms restrict update/cancel only — never create.
    if (mode !== "edit") {
      return allowAll;
    }

    if (isUnavailabilityEdit) {
      return allowAll;
    }

    if (isCustomerUser && !bookingTermsLoaded) {
      return allowAll;
    }

    return evaluateBookingTermsRestrictions(
      bookingTerms,
      minutesUntilBookingStart,
      !isOcpiBooking,
      isCustomerUser,
      t
    );
  }, [
    bookingTerms,
    bookingTermsLoaded,
    isCustomerUser,
    isOcpiBooking,
    isUnavailabilityEdit,
    minutesUntilBookingStart,
    mode,
    t,
  ]);
  const isTermsPending = isCustomerUser && mode === "edit" && !bookingTermsLoaded;
  const isTermsLockedUpdate =
    isCustomerUser &&
    mode === "edit" &&
    (isTermsPending || bookingTermsRestrictions.canUpdate === false);
  const isTermsLockedCancel =
    isCustomerUser &&
    mode === "edit" &&
    (isTermsPending || bookingTermsRestrictions.canCancel === false);
  const isSummaryOnlyEdit =
    mode === "edit" &&
    (isOcpiBooking ||
      isUpdateLockedByCharger ||
      (isTermsLockedUpdate && !isTermsPending));
  const hasTermsWarning =
    mode === "edit" && !bookingTermsLoading && Boolean(bookingTermsRestrictions.termsAlert);
  const isFormReadOnly = isSummaryOnlyEdit && !isCancelFlow;
  const contextBookingId = bookingContext?.booking?.bookingPublicId || null;
  const connectorIdForTerms =
    bookingContext?.connector?.connectorPublicId ||
    bookingContext?.connector?.chargingConnectorPublicId ||
    bookingContext?.booking?.chargingConnectorPublicId ||
    null;
  const isAwaitingEditDetails =
    isOpen &&
    mode === "edit" &&
    Boolean(loadBookingDetails) &&
    Boolean(contextBookingId) &&
    (detailsLoading || resolvedBooking?.bookingPublicId !== contextBookingId);
  const isAwaitingBookingTerms =
    isOpen &&
    mode === "edit" &&
    !isUnavailabilityEdit &&
    Boolean(loadBookingTermsForConnector) &&
    Boolean(connectorIdForTerms) &&
    !bookingTermsLoaded;
  const isEditBootstrapping = isAwaitingEditDetails || isAwaitingBookingTerms;
  const siteOptions = useMemo(() => getSiteOptions(sites, t), [sites, t]);
  const filteredSiteOptions = useMemo(() => {
    const normalizedSearch = siteSearch.trim().toLowerCase();

    if (!normalizedSearch) {
      return siteOptions;
    }

    return siteOptions.filter((option) =>
      String(option.label || "").toLowerCase().includes(normalizedSearch)
    );
  }, [siteOptions, siteSearch]);
  const selectedSite = useMemo(
    () => (Array.isArray(sites) ? sites.find((site) => site.sitePublicId === sitePublicId) : null),
    [sitePublicId, sites]
  );
  const bookingEndDateTime = useMemo(
    () => getBookingEndValue(startDateTime, durationMinutes),
    [durationMinutes, startDateTime]
  );
  const effectiveEndDateTime = bookingType === BOOKING_TYPE_UNAVAILABILITY ? endDateTime : bookingEndDateTime;
  const connectorOptions = useMemo(
    () => availableConnectors.map(getConnectorOption).filter((option) => option.value),
    [availableConnectors]
  );
  const customerOptions = useMemo(() => customers.map((customer) => getCustomerOption(customer, t)), [customers, t]);
  const tokenOptions = useMemo(() => tokens.map((token) => getTokenOption(token, t)), [tokens, t]);
  const bookingTypeOptions = useMemo(
    () => {
      if (isCustomerUser) {
        return [getBookingTypeOption(BOOKING_TYPE_BOOKING, t)];
      }

      if (isTypeLocked) {
        return [getBookingTypeOption(lockedBookingType, t)];
      }

      return [getBookingTypeOption(BOOKING_TYPE_BOOKING, t), getBookingTypeOption(BOOKING_TYPE_UNAVAILABILITY, t)];
    },
    [isCustomerUser, isTypeLocked, lockedBookingType, t]
  );
  const steps = useMemo(
    () => buildSteps(bookingType, isCustomerUser, isTypeLocked),
    [bookingType, isCustomerUser, isTypeLocked]
  );
  const cancelReasonOptions = useMemo(
    () => CANCEL_REASON_OPTIONS.map((value) => getCancelReasonOption(value, t)),
    [t]
  );
  const selectedConnector = useMemo(
    () => availableConnectors.find((connector) => {
      return connector?.connectorPublicId === connectorPublicId;
    }) || null,
    [availableConnectors, connectorPublicId]
  );
  const selectedCustomer = useMemo(
    () => customers.find((customer) => customer?.customerPublicId === customerPublicId) || null,
    [customerPublicId, customers]
  );
  const selectedTokens = useMemo(
    () => tokenOptions.filter((option) => tokenPublicIds.includes(option.value)),
    [tokenOptions, tokenPublicIds]
  );
  const currentStepLabel = steps[step] || steps[0];
  const currentSearchKey = useMemo(
    () => getSearchKey(sitePublicId, startDateTime, effectiveEndDateTime),
    [effectiveEndDateTime, sitePublicId, startDateTime]
  );
  const hasFreshConnectorSearch = availableSearchKey === currentSearchKey;
  const canGoNext =
    (currentStepLabel === "Site" && Boolean(sitePublicId)) ||
    (currentStepLabel === "Time" &&
      Boolean(sitePublicId) &&
      Boolean(startDateTime) &&
      Boolean(effectiveEndDateTime) &&
      Boolean(connectorPublicId) &&
      hasFreshConnectorSearch) ||
    (currentStepLabel === "Type" && Boolean(bookingType)) ||
    (currentStepLabel === "Customer" &&
      (bookingType === BOOKING_TYPE_UNAVAILABILITY ||
        (Boolean(customerPublicId) && tokenPublicIds.length > 0))) ||
    currentStepLabel === "Summary";

  useLayoutEffect(() => {
    if (!isOpen) {
      setStep(0);
      setResolvedBooking(null);
      setIsCancelFlow(false);
      setDetailsLoading(false);
      setBookingTermsLoading(false);
      setBookingTerms(null);
      setBookingTermsLoaded(false);
      return;
    }

    const initialState = getInitialState({
      mode,
      initialSitePublicId: bookingContext?.site?.sitePublicId || "",
      initialStartDate: defaultStartDate,
      initialEndDate: defaultEndDate,
      booking: bookingContext?.booking || null,
      connector: bookingContext?.connector || null,
    });
    const initialConnector = bookingContext?.connector || null;
    const initialConnectors = initialConnector ? [initialConnector] : [];
    const initialSearchKey = getSearchKey(
      initialState.sitePublicId,
      initialState.startDateTime,
      initialState.endDateTime
    );
    const initialBooking = bookingContext?.booking || null;
    const skipBookingTerms = isUnavailabilityBookingType(initialState.bookingType);
    const lockType = mode === "edit";
    const openAsSummaryOnlyEdit =
      mode === "edit" &&
      (isOcpiBookingSource(initialBooking) || getIsChargerNotified(initialBooking) === true);
    const initialStep = openAsSummaryOnlyEdit
      ? buildSteps(initialState.bookingType, isCustomerUser, lockType).length - 1
      : Number.isInteger(bookingContext?.initialStep)
        ? bookingContext.initialStep
        : initialState.step;

    setStep(Math.max(initialStep, 0));
    setSiteSearch("");
    setSitePublicId(initialState.sitePublicId);

    if (initialState.sitePublicId) {
      const allOptions = getSiteOptions(sites, t);
      const idx = allOptions.findIndex((o) => o.value === initialState.sitePublicId);
      setSitePage(idx >= 0 ? Math.floor(idx / SITES_PER_PAGE) : 0);
    } else {
      setSitePage(0);
    }
    setStartDateTime(initialState.startDateTime);
    setDurationMinutes(initialState.durationMinutes);
    setBookingType(isCustomerUser ? BOOKING_TYPE_BOOKING : initialState.bookingType);
    setEndDateTime(initialState.endDateTime);
    setConnectorPublicId(initialState.connectorPublicId);
    setCustomerPublicId(initialState.customerPublicId);
    setTokenPublicIds(initialState.tokenPublicIds);
    setCancelReason("");
    setConfirmCancel(false);
    setIsCancelFlow(false);
    setResolvedBooking(bookingContext?.booking || null);
    setTermsEvaluatedAtMs(Date.now());
    setBookingTerms(null);
    const shouldLoadEditDetails =
      mode === "edit" &&
      Boolean(bookingContext?.booking?.bookingPublicId) &&
      Boolean(loadBookingDetails);
    const shouldLoadBookingTerms =
      mode === "edit" &&
      !skipBookingTerms &&
      Boolean(loadBookingTermsForConnector) &&
      Boolean(
        bookingContext?.connector?.connectorPublicId ||
          bookingContext?.connector?.chargingConnectorPublicId ||
          bookingContext?.booking?.chargingConnectorPublicId
      );
    setDetailsLoading(shouldLoadEditDetails);
    setBookingTermsLoading(shouldLoadBookingTerms);
    setBookingTermsLoaded(!shouldLoadBookingTerms);
    setAvailableConnectors(initialConnectors);
    setAvailableSearchKey(initialConnectors.length > 0 ? initialSearchKey : "");
    setTokens([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, [bookingContext, defaultEndDate, defaultStartDate, isCustomerUser, isOpen, mode]);

  useEffect(() => {
    if (!isOpen || mode !== "edit" || !isSummaryOnlyEdit || isCancelFlow || isEditBootstrapping) {
      return;
    }

    const contextBookingId = bookingContext?.booking?.bookingPublicId || null;
    const activeBookingId = booking?.bookingPublicId || null;

    if (contextBookingId && activeBookingId && contextBookingId !== activeBookingId) {
      return;
    }

    setStep(steps.length - 1);
  }, [
    booking?.bookingPublicId,
    bookingContext?.booking?.bookingPublicId,
    isCancelFlow,
    isEditBootstrapping,
    isOpen,
    isSummaryOnlyEdit,
    mode,
    steps.length,
  ]);

  useEffect(() => {
    if (!isOpen || mode !== "edit" || isEditBootstrapping) {
      return;
    }

    setTermsEvaluatedAtMs(Date.now());
  }, [isEditBootstrapping, isOpen, mode]);

  useEffect(() => {
    if (!isOpen || mode !== "edit" || !loadBookingTermsForConnector) {
      return;
    }

    if (isUnavailabilityBookingType(bookingContext?.booking?.bookingType)) {
      setBookingTerms(null);
      setBookingTermsLoading(false);
      setBookingTermsLoaded(true);
      return;
    }

    const connectorPublicId =
      bookingContext?.connector?.connectorPublicId ||
      bookingContext?.connector?.chargingConnectorPublicId ||
      bookingContext?.booking?.chargingConnectorPublicId ||
      null;

    if (!connectorPublicId) {
      setBookingTerms(null);
      setBookingTermsLoading(false);
      setBookingTermsLoaded(true);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setBookingTermsLoading(true);

      try {
        const terms = await loadBookingTermsForConnector(connectorPublicId);

        if (!cancelled) {
          setBookingTerms(terms ?? null);
        }
      } catch {
        if (!cancelled) {
          setBookingTerms(null);
        }
      } finally {
        if (!cancelled) {
          setBookingTermsLoading(false);
          setBookingTermsLoaded(true);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [bookingContext, isOpen, loadBookingTermsForConnector, mode]);

  useEffect(() => {
    if (!isOpen || mode !== "edit" || !bookingContext?.booking?.bookingPublicId || !loadBookingDetails) {
      return;
    }

    let cancelled = false;

    const load = async () => {
      setDetailsLoading(true);

      try {
        const details = await loadBookingDetails(bookingContext.booking.bookingPublicId);

        if (cancelled || !details) {
          return;
        }

        setResolvedBooking(mergeBookingDetails(bookingContext.booking, details));
      } finally {
        if (!cancelled) {
          setDetailsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [bookingContext, isOpen, loadBookingDetails, mode]);

  useEffect(() => {
    if (!isOpen || bookingType !== BOOKING_TYPE_BOOKING || !loadCustomers) {
      setCustomers([]);
      setCustomersLoading(false);
      return;
    }

    if (!isCustomerUser && !sitePublicId) {
      setCustomers([]);
      setCustomersLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setCustomersLoading(true);

      try {
        const list = await loadCustomers(sitePublicId);

        if (!cancelled) {
          const nextCustomers = Array.isArray(list) ? list : [];
          setCustomers(nextCustomers);

          if (isCustomerUser && nextCustomers.length > 0) {
            setCustomerPublicId((current) => {
              if (current && nextCustomers.some((customer) => customer?.customerPublicId === current)) {
                return current;
              }

              return nextCustomers[0]?.customerPublicId || "";
            });
          }
        }
      } finally {
        if (!cancelled) {
          setCustomersLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [bookingType, isCustomerUser, isOpen, loadCustomers, sitePublicId]);

  useEffect(() => {
    if (!isOpen || bookingType !== BOOKING_TYPE_BOOKING || !customerPublicId || !loadTokens) {
      return;
    }

    let cancelled = false;

    const load = async () => {
      setTokensLoading(true);

      try {
        const list = await loadTokens(customerPublicId);

        if (!cancelled) {
          const normalizedTokens = Array.isArray(list) ? list : [];
          setTokens(normalizedTokens);
          setTokenPublicIds((current) =>
            current.filter((tokenPublicId) =>
              normalizedTokens.some((token) => getTokenOption(token, t).value === tokenPublicId)
            )
          );
        }
      } finally {
        if (!cancelled) {
          setTokensLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [bookingType, customerPublicId, isOpen, loadTokens, t]);

  useEffect(() => {
    if (!isOpen || mode !== "edit" || bookingType !== BOOKING_TYPE_BOOKING || !booking) {
      return;
    }

    if (!customerPublicId && booking?.customerPublicId) {
      setCustomerPublicId(booking.customerPublicId);
    }

    const bookingTokenPublicIds = Array.isArray(booking?.tokenPublicIds)
      ? booking.tokenPublicIds
      : Array.isArray(booking?.tokens)
        ? booking.tokens
          .map((token) => token?.tokenPublicId || token?.idTagPublicId || null)
          .filter(Boolean)
        : [];

    if (tokenPublicIds.length === 0 && bookingTokenPublicIds.length > 0) {
      setTokenPublicIds(bookingTokenPublicIds);
    }
  }, [booking, bookingType, customerPublicId, isOpen, mode, tokenPublicIds.length]);

  useEffect(() => {
    if (bookingType === BOOKING_TYPE_BOOKING) {
      setEndDateTime(bookingEndDateTime);
      return;
    }

    if (!startDateTime || !endDateTime) {
      return;
    }

    const startDate = new Date(startDateTime);
    const endDate = new Date(endDateTime);

    if (!Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime()) && endDate <= startDate) {
      setEndDateTime(toDateTimeLocalValue(addMinutes(startDate, 15)));
    }
  }, [bookingEndDateTime, bookingType, endDateTime, startDateTime]);

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }

    onClose();
  };

  const handleNext = () => {
    setStep((current) => Math.min(current + 1, steps.length - 1));
  };

  const handleBack = () => {
    setStep((current) => Math.max(current - 1, 0));
  };

  const handleStartCancelFlow = () => {
    if (isCancelLockedByCharger || isTermsLockedCancel) {
      return;
    }

    setIsCancelFlow(true);
    if (bookingType === BOOKING_TYPE_UNAVAILABILITY) {
      setCancelReason("UNKNOWN");
    }
    setStep(steps.length - 1);
  };

  const handleReturnToUpdateFlow = () => {
    setIsCancelFlow(false);
    setCancelReason("");
    setConfirmCancel(false);
  };

  const resetAvailableConnectorSearch = () => {
    setConnectorPublicId("");
    setAvailableConnectors([]);
    setAvailableSearchKey("");
  };

  const handleSearchAvailableConnectors = async () => {
    if (!sitePublicId || !startDateTime || !effectiveEndDateTime || !loadAvailableConnectors) {
      return;
    }

    setAvailableConnectorsLoading(true);

    try {
      const excludeBookingPublicId = mode === "edit" ? booking?.bookingPublicId ?? null : null;
      const connectors = await loadAvailableConnectors(sitePublicId, startDateTime, effectiveEndDateTime, excludeBookingPublicId, bookingType);
      const normalizedConnectors = Array.isArray(connectors)
        ? connectors
        : Array.isArray(connectors?.connectors)
          ? connectors.connectors
          : Array.isArray(connectors?.items)
            ? connectors.items
            : [];

      setAvailableConnectors(normalizedConnectors);
      setAvailableSearchKey(currentSearchKey);

      const hasCurrentConnector = normalizedConnectors.some(
        (connector) => getConnectorOption(connector).value === connectorPublicId
      );

      if (!hasCurrentConnector) {
        setConnectorPublicId("");
      }
    } finally {
      setAvailableConnectorsLoading(false);
    }
  };

  const handleCloseConnectorDetails = () => {
    setConnectorDetailsOpen(false);
    setConnectorDetails(null);
    setConnectorDetailsError("");
    setConnectorDetailsLoading(false);
  };

  const handleOpenConnectorDetails = async (connector) => {
    const connectorPublicIdValue = connector?.connectorPublicId;
    if (!connectorPublicIdValue || !loadConnectorDetails) {
      return;
    }

    setConnectorDetailsOpen(true);
    setConnectorDetailsLoading(true);
    setConnectorDetails(null);
    setConnectorDetailsError("");

    try {
      const details = await loadConnectorDetails(connectorPublicIdValue);
      setConnectorDetails(details);
    } catch (error) {
      setConnectorDetailsError(error?.message || t("booking.wizard.failedConnectorDetails"));
    } finally {
      setConnectorDetailsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (isUpdateLockedByCharger || isOcpiBooking) {
      return;
    }

    const effectiveBookingType = isTypeLocked ? lockedBookingType : bookingType;
    const payload = {
      chargingConnectorPublicId: connectorPublicId,
      bookingType: effectiveBookingType,
      startDateTimeUtc: toIsoOrNull(startDateTime),
      endDateTimeUtc: toIsoOrNull(effectiveEndDateTime),
      customerPublicId: effectiveBookingType === BOOKING_TYPE_BOOKING ? customerPublicId || null : null,
      tokenPublicIds: effectiveBookingType === BOOKING_TYPE_BOOKING ? tokenPublicIds : [],
    };

    if (mode === "edit" && booking?.bookingPublicId) {
      await onUpdate(booking.bookingPublicId, payload);
      return;
    }

    await onCreate(payload);
  };

  const handleCancelBooking = async () => {
    if (isCancelLockedByCharger) {
      return;
    }

    if (!booking?.bookingPublicId) {
      return;
    }

    const effectiveCancelReason = bookingType === BOOKING_TYPE_UNAVAILABILITY ? "UNKNOWN" : cancelReason;

    if (!confirmCancel || !effectiveCancelReason) {
      return;
    }

    await onCancelBooking(booking.bookingPublicId, { canceledReason: effectiveCancelReason });
  };

  const renderStepContent = () => {
    if (currentStepLabel === "Site") {
      return (
        <div className="space-y-5">
          <div>
            <h3 className="text-lg font-bold">{t("booking.wizard.chooseSite")}</h3>
          </div>

          <div>
            <label className="mb-2 flex items-center gap-2 text-sm font-medium text-mute-700">
              <FontAwesomeIcon icon={faLocationDot} className="text-primary-500" />
              {t("entity.site")}
            </label>

            <div className="mb-3">
              <input
                type="text"
                className="input-default w-full"
                value={siteSearch}
                onChange={(event) => { setSiteSearch(event.target.value); setSitePage(0); }}
                placeholder={t("booking.wizard.searchSite")}
              />
            </div>

            <div className="space-y-3 rounded-2xl border border-mute-200 bg-mute-50 p-3">
              {(() => {
                const totalPages = Math.ceil(filteredSiteOptions.length / SITES_PER_PAGE);
                const pageStart = sitePage * SITES_PER_PAGE;
                const pageOptions = filteredSiteOptions.slice(pageStart, pageStart + SITES_PER_PAGE);
                const emptySlots = SITES_PER_PAGE - pageOptions.length;

                return (
                  <>
                    {pageOptions.map((option) => {
                      const isSelected = option.value === sitePublicId;

                      return (
                        <label
                          key={option.value}
                          className={`flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 transition ${isSelected ? "border-primary-500 bg-primary-50" : "border-mute-200 bg-white hover:bg-mute-50"}`}
                        >
                          <input
                            type="radio"
                            name="booking-site"
                            className="mt-1"
                            checked={isSelected}
                            onChange={() => {
                              setSitePublicId(option.value);
                              resetAvailableConnectorSearch();
                              setCustomerPublicId("");
                              setTokenPublicIds([]);
                              setCustomers([]);
                              setTokens([]);
                            }}
                          />
                          <div className="min-w-0">
                            <div className="font-semibold">{option.label}</div>
                          </div>
                        </label>
                      );
                    })}

                    {Array.from({ length: emptySlots }, (_, i) => (
                      <div
                        key={`empty-${i}`}
                        className="rounded-2xl border border-transparent px-4 py-3 invisible"
                        aria-hidden
                      >
                        <div className="flex items-start gap-3">
                          <div className="mt-1 w-4" />
                          <div className="font-semibold">&nbsp;</div>
                        </div>
                      </div>
                    ))}

                    {filteredSiteOptions.length === 0 && (
                      <div className="rounded-2xl border border-dashed border-mute-200 bg-white px-4 py-6 text-sm text-mute-500">
                        {t("booking.wizard.noSitesMatch")}
                      </div>
                    )}

                    {totalPages > 1 && (
                      <div className="flex items-center justify-between pt-2">
                        <button
                          type="button"
                          className="btn btn-outline-mute text-sm"
                          disabled={sitePage === 0}
                          onClick={() => setSitePage((p) => p - 1)}
                        >
                          {t("common.previous")}
                        </button>
                        <span className="text-sm text-mute-500">
                          {sitePage + 1} / {totalPages}
                        </span>
                        <button
                          type="button"
                          className="btn btn-outline-mute text-sm"
                          disabled={sitePage >= totalPages - 1}
                          onClick={() => setSitePage((p) => p + 1)}
                        >
                          {t("common.next")}
                        </button>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      );
    }

    if (currentStepLabel === "Type") {
      return (
        <div className="space-y-5">
          <div>
            <h3 className="text-lg font-bold">{t("booking.wizard.chooseType")}</h3>
            <p className="mt-1 text-sm text-mute-500">
              {isTypeLocked
                ? t("booking.wizard.typeLocked")
                : t("booking.wizard.typeHelp")}
            </p>
          </div>

          <div className="grid gap-3">
            {bookingTypeOptions.map((option) => {
              const isSelected = bookingType === option.value;
              const icon = option.value === BOOKING_TYPE_UNAVAILABILITY ? faWrench : faCalendarPlus;

              return (
                <button
                  key={option.value}
                  type="button"
                  className={`rounded-2xl border px-4 py-4 text-left transition ${isSelected ? "border-primary-500 bg-primary-50" : "border-mute-200 bg-white hover:bg-mute-50"} ${isTypeLocked ? "cursor-default" : ""}`}
                  disabled={isTypeLocked && option.value !== bookingType}
                  onClick={() => {
                    if (isTypeLocked) {
                      return;
                    }

                    setBookingType(option.value);
                    resetAvailableConnectorSearch();

                    if (option.value !== BOOKING_TYPE_BOOKING) {
                      setCustomerPublicId("");
                      setTokenPublicIds([]);
                    }
                  }}
                >
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon={icon} className="text-primary-600" />
                    <div>
                      <div className="font-semibold">{option.label}</div>
                      <div className="text-sm text-mute-500">
                        {option.value === BOOKING_TYPE_UNAVAILABILITY
                          ? t("booking.wizard.typeUnavailabilityHelp")
                          : t("booking.wizard.typeBookingHelp")}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    if (currentStepLabel === "Time") {
      return (
        <div className="space-y-5">
          <div>
            <h3 className="text-lg font-bold">{t("booking.wizard.chooseTimeAndConnector")}</h3>
            <p className="mt-1 text-sm text-mute-500">
              {bookingType === BOOKING_TYPE_UNAVAILABILITY
                ? t("booking.wizard.timeUnavailabilityHelp")
                : t("booking.wizard.timeBookingHelp")}
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-3 md:items-end">
            <div className="min-w-0">
              <label className="mb-2 flex items-center gap-2 text-sm font-medium text-mute-700">
                <FontAwesomeIcon icon={faClock} className="text-primary-500" />
                {t("common.start")}
              </label>
              <input
                type="datetime-local"
                step={bookingType === BOOKING_TYPE_UNAVAILABILITY ? "60" : "900"}
                className="input-default w-full min-w-0 max-w-full appearance-none"
                style={{ minWidth: 0, maxWidth: "100%" }}
                value={startDateTime}
                onChange={(event) => {
                  setStartDateTime(event.target.value);
                  resetAvailableConnectorSearch();
                }}
                onBlur={(event) => {
                  if (bookingType === BOOKING_TYPE_BOOKING || bookingType === BOOKING_TYPE_UNAVAILABILITY) {
                    setStartDateTime(roundUpToQuarter(event.target.value));
                  }
                }}
              />
            </div>

            {bookingType === BOOKING_TYPE_UNAVAILABILITY ? (
              <div className="min-w-0">
                <label className="mb-2 flex items-center gap-2 text-sm font-medium text-mute-700">
                  <FontAwesomeIcon icon={faClock} className="text-primary-500" />
                  {t("common.end")}
                </label>
                <input
                  type="datetime-local"
                  step="60"
                  className="input-default w-full min-w-0 max-w-full appearance-none"
                  style={{ minWidth: 0, maxWidth: "100%" }}
                  value={endDateTime}
                  onChange={(event) => {
                    setEndDateTime(event.target.value);
                    resetAvailableConnectorSearch();
                  }}
                  onBlur={(event) => {
                    setEndDateTime(roundUpToQuarter(event.target.value));
                  }}
                />
              </div>
            ) : (
              <div className="min-w-0">
                <label className="mb-2 flex items-center gap-2 text-sm font-medium text-mute-700">
                  <FontAwesomeIcon icon={faClock} className="text-primary-500" />
                  {t("booking.wizard.duration")}
                </label>
                <SelectDropdown
                  options={DURATION_OPTIONS.map((minutes) => ({
                    value: minutes,
                    label: formatDuration(minutes, t),
                  }))}
                  selectedValues={DURATION_OPTIONS
                    .filter((minutes) => minutes === Number(durationMinutes))
                    .map((minutes) => ({ value: minutes, label: formatDuration(minutes, t) }))}
                  onChange={(selected) => {
                    setDurationMinutes(selected?.value || 30);
                    resetAvailableConnectorSearch();
                  }}
                  isMulti={false}
                  placeholder={t("booking.wizard.selectDuration")}
                  isClearable={false}
                />
              </div>
            )}

            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSearchAvailableConnectors}
              disabled={!sitePublicId || !startDateTime || !effectiveEndDateTime || isSubmitting || availableConnectorsLoading}
            >
              {availableConnectorsLoading ? (
                <FontAwesomeIcon icon={faSpinnerThird} spin />
              ) : (
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              )}
              {t("common.search")}
            </button>
          </div>

          <div className="rounded-xl border border-mute-200 bg-mute-50 px-4 py-3 text-sm text-mute-700">
            <span className="font-medium">{t("common.end")}:</span> {effectiveEndDateTime ? format(new Date(effectiveEndDateTime), "yyyy-MM-dd HH:mm") : t("booking.wizard.selectStartAndSpan")}
          </div>

          <div>
            <label className="mb-2 flex items-center gap-2 text-sm font-medium text-mute-700">
              <FontAwesomeIcon icon={faPlug} className="text-primary-500" />
              {t("booking.wizard.availableConnector")}
            </label>
            {bookingType === BOOKING_TYPE_BOOKING && (
              <p className="mb-2 text-xs text-mute-500">
                {t("booking.wizard.connectorBufferHelp")}
              </p>
            )}
            <div className="space-y-3 rounded-2xl border border-mute-200 bg-mute-50 p-3">
              {!availableConnectorsLoading && connectorOptions.length === 0 && !hasFreshConnectorSearch && (
                <div className="rounded-2xl border border-dashed border-mute-200 bg-white px-4 py-6 text-sm text-mute-500">
                  {t("booking.wizard.searchToLoadConnectors")}
                </div>
              )}

              {!availableConnectorsLoading && hasFreshConnectorSearch && connectorOptions.length === 0 && (
                <div className="rounded-2xl border border-dashed border-rose-200 bg-white px-4 py-6 text-sm text-rose-600">
                  {t("booking.wizard.noAvailableConnectors")}
                </div>
              )}

              {connectorOptions.map((option) => {
                const isSelected = option.value === connectorPublicId;
                const connectorMeta =
                  option.connector?.ocpiEvseId ??
                  option.connector?.connectorPublicId ??
                  "-";
                const connectorPowerSummary = formatConnectorPowerSummary(option.connector);

                return (
                  <div
                    key={option.value}
                    className={`flex items-start gap-3 rounded-2xl border px-4 py-3 transition ${isSelected ? "border-primary-500 bg-primary-50" : "border-mute-200 bg-white hover:bg-mute-50"}`}
                  >
                    <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-3">
                      <input
                        type="radio"
                        name="available-connector"
                        className="mt-1"
                        checked={isSelected}
                        onChange={() => setConnectorPublicId(option.value)}
                      />
                      <div className="min-w-0">
                        <div className="font-semibold">{option.label}</div>
                        {connectorPowerSummary && (
                          <div className="mt-1 text-sm text-mute-700">{connectorPowerSummary}</div>
                        )}
                        <div className="mt-1 text-xs text-mute-500">
                          {connectorMeta}
                        </div>
                      </div>
                    </label>
                    {loadConnectorDetails && (
                      <button
                        type="button"
                        className="btn btn-outline-primary shrink-0 px-2 py-1 text-xs"
                        aria-label={t("booking.wizard.showDetailsFor", { name: option.label })}
                        onClick={() => handleOpenConnectorDetails(option.connector)}
                      >
                        <FontAwesomeIcon icon={faCircleInfo} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    }

    if (currentStepLabel === "Customer") {
      return (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-bold">
              {isCustomerUser ? t("booking.wizard.chooseTokens") : t("booking.wizard.chooseCustomerAndTokens")}
            </h3>
            <p className="mt-1 text-sm text-mute-500">
              {isCustomerUser
                ? t("booking.wizard.tokensOwnHelp")
                : t("booking.wizard.tokensHelp")}
            </p>
          </div>

          {(!isCustomerUser || customerOptions.length > 1) && (
            <div>
              <label className="mb-2 flex items-center gap-2 text-sm font-medium text-mute-700">
                <FontAwesomeIcon icon={faUser} className="text-primary-500" />
                {t("entity.customer")}
              </label>
              <SelectDropdown
                options={customerOptions}
                selectedValues={customerOptions.filter((option) => option.value === customerPublicId)}
                onChange={(selected) => {
                  setCustomerPublicId(selected?.value || "");
                  setTokenPublicIds([]);
                }}
                isMulti={false}
                placeholder={customersLoading ? t("chargeMonitor.loadingCustomers") : t("chargeMonitor.selectCustomer")}
                disabled={customersLoading}
                isClearable={!isCustomerUser}
              />
            </div>
          )}

          {isCustomerUser && customerOptions.length <= 1 && (
            <div className="rounded-xl border border-mute-200 bg-mute-50 px-4 py-3 text-sm text-mute-700">
              <span className="font-semibold">{t("entity.customer")}:</span>{" "}
              {getBookingCustomerLabel(booking, selectedCustomer)}
            </div>
          )}

          <div>
            <label className="mb-2 flex items-center gap-2 text-sm font-medium text-mute-700">
              <FontAwesomeIcon icon={faIdCard} className="text-primary-500" />
              {t("entity.tokens")}
            </label>
            <SelectDropdown
              options={tokenOptions}
              selectedValues={selectedTokens}
              onChange={(selected) => setTokenPublicIds(Array.isArray(selected) ? selected.map((item) => item.value) : [])}
              isMulti
              placeholder={tokensLoading ? t("chargeMonitor.loadingTokens") : t("booking.wizard.selectOneOrMoreTokens")}
              disabled={tokensLoading || !customerPublicId}
            />
            {!tokensLoading && customerPublicId && tokenOptions.length === 0 && (
              <p className="mt-2 text-sm text-rose-600">{t("booking.wizard.noTokensForCustomer")}</p>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-5">
        <div>
          <h3 className="text-lg font-bold">{isCancelFlow ? t("booking.wizard.cancelTitle") : isSummaryOnlyEdit ? t("booking.wizard.detailsTitle") : t("booking.step.Summary")}</h3>
          <p className="mt-1 text-sm text-mute-500">
            {isCancelFlow
              ? t("booking.wizard.cancelConfirmHelp")
              : isOcpiBooking
              ? t("booking.wizard.ocpiCancelOnly")
              : isSummaryOnlyEdit
              ? isCancelLockedByCharger
                ? t("booking.wizard.noUpdateNoCancel")
                : t("booking.wizard.cancelOnly")
              : t("booking.wizard.reviewBeforeSave")}
          </p>
        </div>

        {!isCancelFlow && (
          <div className="rounded-2xl border border-mute-200 bg-mute-50 p-4 text-sm text-mute-700">
            <div className="grid gap-2 md:grid-cols-2">
              <div><span className="font-semibold">{t("entity.site")}:</span> {selectedSite?.siteName || "-"}</div>
              <div><span className="font-semibold">{t("entity.connector")}:</span> {selectedConnector?.connectorName || "-"}</div>
              <div><span className="font-semibold">{t("common.start")}:</span> {startDateTime ? format(new Date(startDateTime), "yyyy-MM-dd HH:mm") : "-"}</div>
              <div><span className="font-semibold">{t("common.end")}:</span> {effectiveEndDateTime ? format(new Date(effectiveEndDateTime), "yyyy-MM-dd HH:mm") : "-"}</div>
              <div><span className="font-semibold">{t("common.type")}:</span> {bookingType === BOOKING_TYPE_UNAVAILABILITY ? t("booking.unavailability") : t("entity.booking")}</div>
              {bookingType === BOOKING_TYPE_BOOKING && (
                <>
                  <div><span className="font-semibold">{t("entity.customer")}:</span> {getBookingCustomerLabel(booking, selectedCustomer)}</div>
                  <div><span className="font-semibold">{t("entity.tokens")}:</span> {selectedTokens.length ? selectedTokens.map((token) => token.label).join(", ") : "-"}</div>
                </>
              )}
            </div>
          </div>
        )}

        {mode === "edit" && isCancelFlow && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-rose-700">
              <FontAwesomeIcon icon={faBan} />
                {t("booking.wizard.cancelTitle")}
              </div>

            <div className="mb-4 rounded-xl border border-rose-200 bg-white px-4 py-3 text-sm text-mute-700">
              <div className="font-semibold">{t("booking.wizard.cancelAboutTo")}</div>
              <div className="mt-2"><span className="font-semibold">{t("entity.site")}:</span> {selectedSite?.siteName || "-"}</div>
              <div><span className="font-semibold">{t("entity.connector")}:</span> {selectedConnector?.connectorName || "-"}</div>
              <div><span className="font-semibold">{t("booking.step.Time")}:</span> {startDateTime ? format(new Date(startDateTime), "yyyy-MM-dd HH:mm") : "-"} - {effectiveEndDateTime ? format(new Date(effectiveEndDateTime), "yyyy-MM-dd HH:mm") : "-"}</div>
              <div><span className="font-semibold">{t("common.type")}:</span> {bookingType === BOOKING_TYPE_UNAVAILABILITY ? t("booking.unavailability") : t("entity.booking")}</div>
              {bookingType === BOOKING_TYPE_BOOKING && (
                <div><span className="font-semibold">{t("entity.customer")}:</span> {getBookingCustomerLabel(booking, selectedCustomer)}</div>
              )}
            </div>

              {bookingType === BOOKING_TYPE_BOOKING ? (
                <div>
                  <label className="mb-2 flex items-center gap-2 text-sm font-medium text-rose-700">
                    <FontAwesomeIcon icon={faWrench} />
                    {t("booking.wizard.cancellationReason")}
                  </label>
                  <SelectDropdown
                    options={cancelReasonOptions}
                    selectedValues={cancelReasonOptions.filter((option) => option.value === cancelReason)}
                    onChange={(selected) => setCancelReason(selected?.value || "")}
                    isMulti={false}
                    placeholder={t("booking.wizard.selectCancelReason")}
                  />
                </div>
              ) : (
                <div className="text-sm text-rose-700">
                  <span className="font-medium">{t("booking.wizard.cancellationReason")}:</span> {t("booking.cancelReason.UNKNOWN")}
                </div>
              )}

              <label className="mt-4 flex items-center gap-3 text-sm text-rose-700">
                <input
                  type="checkbox"
                  checked={confirmCancel}
                  onChange={(event) => setConfirmCancel(event.target.checked)}
                />
                {t("booking.wizard.confirmCancelCheckbox")}
              </label>
            </div>
        )}
      </div>
    );
  };

  return (
    <>
    <ModalBase
      isOpen={isOpen}
      onClose={handleClose}
      closeOnOutsideClick={!connectorDetailsOpen}
      size="lg"
      className="px-4 py-4 sm:px-8 sm:py-8"
    >
      <div className="flex h-[calc(100dvh-4rem)] min-h-0 flex-col gap-2 sm:h-[80vh] sm:gap-3">
        <div className="flex shrink-0 items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-primary-700">
              {mode === "edit"
                ? isOcpiBooking
                  ? t("booking.wizard.ocpiBooking")
                  : isSummaryOnlyEdit
                  ? t("booking.wizard.detailsTitle")
                  : t("booking.wizard.updateTitle")
                : t("booking.wizard.createTitle")}
            </h2>
            <p className="mt-0.5 text-xs text-mute-500 sm:text-sm">
              {isEditBootstrapping
                ? isUnavailabilityEdit
                  ? t("booking.loadingUnavailability")
                  : t("booking.loadingBookingAndTerms")
                : isChargerNotified && isReservedStatus
                ? t("booking.wizard.chargerCancelOnly")
                : isChargerNotified
                ? t("booking.wizard.chargerBlocked")
                : isOcpiBooking
                ? t("booking.wizard.ocpiOnlyCancelHere")
                : hasTermsWarning && isCustomerUser
                ? t("booking.wizard.blockedByTerms")
                : hasTermsWarning
                ? t("booking.wizard.breaksTerms")
                : mode === "edit"
                ? isUnavailabilityEdit
                  ? t("booking.wizard.updateUnavailabilityHelp")
                  : t("booking.wizard.updateHelp")
                : t("booking.wizard.createHelp")}
            </p>
          </div>
          {!isEditBootstrapping && isSubmitting && (
            <FontAwesomeIcon icon={faSpinnerThird} spin className="mt-1 text-lg text-primary-500" />
          )}
        </div>

        {isEditBootstrapping ? (
          <div className="flex min-h-0 flex-1 items-center justify-center rounded-2xl border border-dashed border-mute-200 bg-white px-6 py-12">
            <LoadingSpinner text={t("booking.loadingBooking")} />
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1">
            <div className="flex flex-col gap-3 pb-1 sm:gap-4">
              {!isChargerNotified && isOcpiBooking && !isCancelFlow && (
                <div className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800">
                  <div className="flex items-start gap-2">
                    <FontAwesomeIcon icon={faCalendarPlus} className="mt-0.5 shrink-0" />
                    <div>
                      <div className="font-semibold">{t("booking.wizard.ocpiBooking")}</div>
                      <div>
                        {t("booking.wizard.ocpiCreatedCannotUpdate")}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {isChargerNotified && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  <div className="flex items-start gap-2">
                    <FontAwesomeIcon icon={faBan} className="mt-0.5 shrink-0" />
                    <div>
                      <div className="font-semibold">{t("booking.wizard.sentToCharger")}</div>
                      <div>
                        {isReservedStatus
                          ? t("booking.wizard.activeReservation")
                          : t("booking.wizard.sentCannotChange")}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {mode === "edit" && !isChargerNotified && bookingTermsRestrictions.termsAlert && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  <div className="flex items-start gap-2">
                    <FontAwesomeIcon icon={faClock} className="mt-0.5 shrink-0" />
                    <div className="space-y-1">
                      <div className="font-semibold">{bookingTermsRestrictions.termsAlert.title}</div>
                      {bookingTermsRestrictions.termsAlert.lines.map((line) => (
                        <div key={line}>{line}</div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {!isSummaryOnlyEdit && (
              <div className={`grid gap-1.5 sm:gap-3 ${steps.length <= 4 ? "grid-cols-4" : "grid-cols-5"}`}>
                {steps.map((label, index) => {
                  const isCurrent = step === index;

                  return (
                    <div key={label} className="flex min-w-0 flex-col items-center text-center">
                      <div
                        className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition ${
                          isCurrent ? "bg-blue-500 text-white" : "bg-mute-200 text-mute-600"
                        }`}
                      >
                        {index + 1}
                      </div>
                      <div className="mt-1 text-xs font-semibold text-mute-700 sm:mt-2 sm:text-sm">
                        {t(`booking.step.${label}`)}
                      </div>
                    </div>
                  );
                })}
              </div>
              )}

              <div
                className={
                  isFormReadOnly
                    ? "pointer-events-none select-none opacity-70"
                    : undefined
                }
              >
                {renderStepContent()}
              </div>
            </div>
          </div>
        )}

        <div className="mt-auto flex w-full shrink-0 flex-row flex-nowrap items-center justify-between gap-2 overflow-x-auto border-t border-mute-200 pt-4 sm:overflow-visible sm:gap-3">
          <div className="flex shrink-0 flex-nowrap items-center gap-2 sm:flex-wrap sm:gap-3">
            <button
              type="button"
              className="btn btn-outline-primary shrink-0 whitespace-nowrap justify-center"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              {t("common.close")}
            </button>
            {!isEditBootstrapping && step > 0 && !(isSummaryOnlyEdit && !isCancelFlow) && (
              <button
                type="button"
                className="btn btn-outline-primary shrink-0 whitespace-nowrap justify-center"
                onClick={handleBack}
                disabled={isSubmitting}
              >
                {t("common.back")}
              </button>
            )}
          </div>

          {!isEditBootstrapping && (
            <div className="ml-auto flex shrink-0 flex-nowrap items-center gap-2 sm:flex-wrap sm:items-center sm:justify-end sm:gap-3">
              {mode === "edit" && isCancelFlow && (
                <button
                  type="button"
                  className="btn btn-outline-primary shrink-0 whitespace-nowrap justify-center"
                  onClick={handleReturnToUpdateFlow}
                  disabled={isSubmitting}
                >
                  {isSummaryOnlyEdit ? t("common.back") : t("booking.wizard.backToUpdate")}
                </button>
              )}

              {mode === "edit" && !isCancelFlow && (step < steps.length - 1 || isSummaryOnlyEdit) && (
                <button
                  type="button"
                  className="btn btn-outline-error shrink-0 whitespace-nowrap justify-center"
                  onClick={handleStartCancelFlow}
                  disabled={
                    isSubmitting ||
                    isCancelLockedByCharger ||
                    isTermsLockedCancel ||
                    !booking?.bookingPublicId
                  }
                >
                  {t("booking.wizard.cancelTitle")}
                </button>
              )}

              {mode === "edit" && isCancelFlow && (
                <button
                  type="button"
                  className="btn btn-outline-error shrink-0 whitespace-nowrap justify-center"
                  onClick={handleCancelBooking}
                  disabled={
                    isSubmitting ||
                    isCancelLockedByCharger ||
                    isTermsLockedCancel ||
                    !booking?.bookingPublicId ||
                    !confirmCancel ||
                    (bookingType === BOOKING_TYPE_BOOKING && !cancelReason)
                  }
                >
                  {t("booking.wizard.cancelTitle")}
                </button>
              )}

              {step < steps.length - 1 ? (
                <button
                  type="button"
                  className="btn btn-primary shrink-0 whitespace-nowrap justify-center"
                  onClick={handleNext}
                  disabled={!canGoNext || isSubmitting || isTermsLockedUpdate}
                >
                  {t("common.next")}
                </button>
              ) : !isCancelFlow && !isSummaryOnlyEdit ? (
                <button
                  type="button"
                  className="btn btn-primary shrink-0 whitespace-nowrap justify-center"
                  onClick={handleSubmit}
                  disabled={
                    isSubmitting ||
                    isUpdateLockedByCharger ||
                    isTermsLockedUpdate ||
                    !canGoNext
                  }
                >
                  {mode === "edit" ? t("common.saveChanges") : t("booking.wizard.createBooking")}
                </button>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </ModalBase>

    <ModalBase
      isOpen={connectorDetailsOpen}
      onClose={handleCloseConnectorDetails}
      zIndex={1100}
      size="lg"
      className="px-4 py-4 sm:px-8 sm:py-8"
    >
      <div className="flex h-[calc(100dvh-4rem)] min-h-0 flex-col gap-4 overflow-hidden sm:h-[85vh]">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-primary-700 sm:text-2xl">{t("booking.wizard.connectorDetails")}</h2>
            <p className="mt-1 text-sm text-gray-500">
              {t("booking.wizard.connectorDetailsHelp")}
            </p>
          </div>
          {connectorDetailsLoading && (
            <FontAwesomeIcon icon={faSpinnerThird} spin className="mt-1 text-lg text-primary-500" />
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          {connectorDetailsLoading && (
            <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-6 py-12 text-center text-sm text-gray-500">
              <LoadingSpinner text={t("booking.loadingConnectorDetails")} />
            </div>
          )}

          {!connectorDetailsLoading && connectorDetailsError && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {connectorDetailsError}
            </div>
          )}

          {!connectorDetailsLoading && !connectorDetailsError && connectorDetails && (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
              <FormCard
                title={connectorDetails.chargingConnectorName ?? connectorDetails.connectorName ?? t("entity.connector")}
                className="min-w-0"
                bodyClassName="pt-0"
              >
                <DetailRow label={t("booking.wizard.chargingPoint")} value={connectorDetails.chargingPointName} />
                <DetailRow label={t("booking.wizard.evseId")} value={connectorDetails.ocpiEvseId} />
                <DetailRow
                  label={t("booking.wizard.outletType")}
                  value={formatConnectorLabelValue(connectorDetails.connectorStandard)}
                />
                <DetailRow
                  label={t("booking.wizard.format")}
                  value={formatConnectorLabelValue(connectorDetails.connectorFormat)}
                />
                <DetailRow
                  label={t("booking.wizard.powerType")}
                  value={formatConnectorLabelValue(connectorDetails.powerType)}
                />
                <DetailRow
                  label={t("booking.wizard.maxPower")}
                  value={
                    connectorDetails.maxElectricPower != null
                      ? `${connectorDetails.maxElectricPower} kW`
                      : null
                  }
                />
              </FormCard>

              <FormCard title={t("entity.parking")} className="min-w-0" bodyClassName="pt-0">
                {Array.isArray(connectorDetails.parkings) && connectorDetails.parkings.length > 0 ? (
                  connectorDetails.parkings.map((parking, index) => {
                    const parkingKey = parking.parkingPublicId ?? parking.physicalReference ?? `parking-${index}`;
                    const positionLabel = formatEvsePositionLabel(parking.evsePosition, t);
                    const driveThroughLabel = formatBooleanLabel(
                      parking.driveThrough,
                      t("booking.wizard.driveThrough"),
                      t("booking.wizard.noDriveThrough")
                    );
                    const refrigerationLabel = formatBooleanLabel(
                      parking.refrigerationOutlet,
                      t("booking.wizard.refrigerationOutlet"),
                      t("booking.wizard.noRefrigerationOutlet")
                    );
                    const roofedLabel = formatBooleanLabel(parking.roofed, t("booking.wizard.roofed"), t("booking.wizard.open"));
                    const lightingLabel = formatBooleanLabel(parking.lighting, t("booking.wizard.lighting"), t("booking.wizard.noLighting"));

                    return (
                      <FormSection
                        key={parkingKey}
                        title={parking.physicalReference || parking.parkingPublicId || t("booking.wizard.parkingN", { n: index + 1 })}
                        className={index > 0 ? "mt-8 border-t border-gray-200 pt-8" : ""}
                        titleClassName="text-base font-semibold text-gray-900"
                      >
                        <DetailRow label={t("booking.wizard.outletSide")} value={positionLabel} />
                        <DetailRow
                          label={t("booking.wizard.spaceLength")}
                          value={parking.parkingSpaceLength != null ? `${parking.parkingSpaceLength} cm` : null}
                        />
                        <DetailRow
                          label={t("booking.wizard.spaceWidth")}
                          value={parking.parkingSpaceWidth != null ? `${parking.parkingSpaceWidth} cm` : null}
                        />
                        <DetailRow
                          label={t("booking.wizard.direction")}
                          value={formatConnectorLabelValue(parking.direction)}
                        />
                        <DetailRow label={t("booking.wizard.driveThroughLabel")} value={driveThroughLabel} />
                        <DetailRow label={t("booking.wizard.refrigeration")} value={refrigerationLabel} />
                        <DetailRow label={t("booking.wizard.roof")} value={roofedLabel} />
                        <DetailRow label={t("booking.wizard.lighting")} value={lightingLabel} />
                      </FormSection>
                    );
                  })
                ) : (
                  <p className="text-sm text-gray-500">
                    {t("booking.wizard.noParkingInfo")}
                  </p>
                )}
              </FormCard>
            </div>
          )}
        </div>

        <div className="mt-auto flex w-full flex-row flex-nowrap items-center justify-between gap-2 overflow-x-auto border-t border-gray-200 pt-4 sm:overflow-visible">
          <button
            type="button"
            className="btn btn-outline-primary shrink-0 whitespace-nowrap justify-center"
            onClick={handleCloseConnectorDetails}
          >
            {t("common.close")}
          </button>
        </div>
      </div>
    </ModalBase>
    </>
  );
};

export default BookingWizardModal;
